import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Flag, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { value: 'inappropriate_content', label: { es: 'Contenido inapropiado', it: 'Contenuto inappropriato' } },
  { value: 'false_information', label: { es: 'Información falsa/engañosa', it: 'Informazioni false/ingannevoli' } },
  { value: 'spam', label: { es: 'Spam/publicidad', it: 'Spam/pubblicità' } },
  { value: 'fake_profile', label: { es: 'Perfil falso', it: 'Profilo fake' } },
  { value: 'unprofessional_behavior', label: { es: 'Comportamiento inadecuado del profesional', it: 'Comportamento scorretto del professionista' } },
  { value: 'other', label: { es: 'Otro', it: 'Altro' } }
];

export default function ReportButton({ contentType, contentId, contentTitle }) {
  const { i18n, t } = useTranslation();
  const { user, token } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [reportType, setReportType] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const lang = i18n.language?.startsWith('it') ? 'it' : 'es';

  const labels = {
    es: {
      report: 'Reportar problema',
      title: 'Reportar este contenido',
      subtitle: 'Selecciona el tipo de problema que has encontrado',
      selectType: 'Tipo de problema',
      selectPlaceholder: 'Selecciona una opción',
      description: 'Descripción (opcional)',
      descriptionPlaceholder: 'Describe el problema con más detalle...',
      submit: 'Enviar reporte',
      cancel: 'Cancelar',
      alreadyReported: 'Ya has reportado este contenido',
      loginRequired: 'Inicia sesión para reportar',
      successTitle: '¡Reporte enviado!',
      successMessage: 'Gracias por ayudarnos a mantener la comunidad segura. Revisaremos tu reporte lo antes posible.',
      close: 'Cerrar'
    },
    it: {
      report: 'Segnala problema',
      title: 'Segnala questo contenuto',
      subtitle: 'Seleziona il tipo di problema riscontrato',
      selectType: 'Tipo di problema',
      selectPlaceholder: 'Seleziona un\'opzione',
      description: 'Descrizione (opzionale)',
      descriptionPlaceholder: 'Descrivi il problema in dettaglio...',
      submit: 'Invia segnalazione',
      cancel: 'Annulla',
      alreadyReported: 'Hai già segnalato questo contenuto',
      loginRequired: 'Accedi per segnalare',
      successTitle: 'Segnalazione inviata!',
      successMessage: 'Grazie per aiutarci a mantenere la community sicura. Esamineremo la tua segnalazione al più presto.',
      close: 'Chiudi'
    }
  };

  const l = labels[lang];

  // Reset state when content changes
  useEffect(() => {
    setSubmitted(false);
    setReportType('');
    setDescription('');
    setIsOpen(false);
  }, [contentId, contentType]);

  // Check if user has already reported this content
  useEffect(() => {
    const checkReportStatus = async () => {
      if (!user || !token) {
        setCheckingStatus(false);
        return;
      }

      setCheckingStatus(true);
      try {
        const response = await axios.get(
          `${API_URL}/api/reports/check/${contentType}/${contentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setHasReported(response.data.has_reported);
      } catch (error) {
        console.error('Error checking report status:', error);
        setHasReported(false);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkReportStatus();
  }, [user, token, contentType, contentId]);

  const handleSubmit = async () => {
    if (!reportType) {
      toast.error(lang === 'it' ? 'Seleziona un tipo di problema' : 'Selecciona un tipo de problema');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/reports`,
        {
          content_type: contentType,
          content_id: contentId,
          report_type: reportType,
          description: description || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSubmitted(true);
      setHasReported(true);
    } catch (error) {
      console.error('Error submitting report:', error);
      const errorMsg = error.response?.data?.detail || (lang === 'it' ? 'Errore nell\'invio della segnalazione' : 'Error al enviar el reporte');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSubmitted(false);
    setReportType('');
    setDescription('');
  };

  // Show login prompt if not logged in
  if (!user) {
    return (
      <Link to="/login" className="inline-flex items-center gap-2 text-gray-500 hover:text-primary text-sm transition-colors">
        <Flag className="w-4 h-4" />
        <span>{l.loginRequired}</span>
      </Link>
    );
  }

  // Don't show if already reported
  if (hasReported && !isOpen) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm">
        <CheckCircle className="w-4 h-4" />
        <span>{l.alreadyReported}</span>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="text-gray-500 hover:text-red-500 hover:bg-red-50"
        disabled={checkingStatus}
        data-testid="report-button"
      >
        <Flag className="w-4 h-4 mr-2" />
        {l.report}
      </Button>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          {!submitted ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  {l.title}
                </DialogTitle>
                <p className="text-sm text-gray-500 mt-1">{l.subtitle}</p>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Report Type Select */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{l.selectType}</label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger data-testid="report-type-select">
                      <SelectValue placeholder={l.selectPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label[lang]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Description Textarea */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{l.description}</label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={l.descriptionPlaceholder}
                    rows={3}
                    className="resize-none"
                    data-testid="report-description"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={handleClose}>
                  {l.cancel}
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={loading || !reportType}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="report-submit"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  ) : (
                    <Flag className="w-4 h-4 mr-2" />
                  )}
                  {l.submit}
                </Button>
              </div>
            </>
          ) : (
            /* Success State */
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{l.successTitle}</h3>
              <p className="text-gray-500 text-sm mb-6">{l.successMessage}</p>
              <Button onClick={handleClose} data-testid="report-close">
                {l.close}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
